/**
 * @param Modal Base widget for extending
 * @returns RefinementsPanel widget
 */
function RefinementsPanelClassCreator(Modal: ReturnType<typeof import('widgets/global/Modal').default>) {
    /**
     * @category widgets
     * @subcategory search
     * @class RefinementsPanel
     * @augments Modal
     * @classdesc Enclose refinement aside into dialog component on sm-md-lg breakpoints
     *
     * @property {string} data-widget - Widget name "refinementsPanel"
     * @property {boolean} data-search-has-results - check if we have some search result. Otherwise, we close dialog
     *
     * @example <caption>Example of RefinementsPanel widget usage</caption>
     * <aside
     *    class="l-plp-refinements_slide b-refinements_slide_panel ${!searchHasProducts ? 'm-no_results' : ''}"
     *    data-widget="refinementsPanel"
     *    data-forward-to-parent="updateView"
     *    data-widget-event-noresult="handleNoResult"
     *    data-search-has-results="${searchHasProducts}"
     *>
     *      ...
     * </aside>
     */
    class RefinementsPanel extends Modal {
        isFirstTimeOpen = true;

        prefs() {
            return {
                searchHasResults: true,
                ...super.prefs()
            };
        }

        /**
         * @description Widget logic initialization
         * @listens "refinement.panel.open"
         */
        init() {
            super.init();
            this.eventBus().on('refinement.panel.open', 'openPanel');
        }

        /**
         * @description Open dialog
         */
        openPanel() {
            this.showModal();
        }

        /**
         * @description Close dialog
         * @emits "refinement.panel.close"
         */
        closePanel() {
            /**
             * @description Global event to close refinement panel
             * @event "refinement.panel.close"
             */
            this.eventBus().emit('refinement.panel.close');
            // Modal.ts method
            this.closeModal();
        }

        /**
         * @description Restore dialog classes when new HTML applied to PLP
         */
        onRefresh() {
            if (this.isDialogOpen && this.prefs().searchHasResults) {
                // AccessibilityFocusTrapMixin.ts method
                if (typeof this.addFocusTraps === 'function') {
                    this.addFocusTraps(true);
                }
            } else {
                this.closePanel();
            }
        }
    }

    return RefinementsPanel;
}

export type TRefinementsPanel = ReturnType<typeof RefinementsPanelClassCreator>;

export type TRefinementsPanelInstance = InstanceType<TRefinementsPanel>;

export default RefinementsPanelClassCreator;
